import { useTranslation } from 'react-i18next';
import { useWindowDimensions, Platform } from 'react-native';
import { DropdownOption } from '@shared/components/ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

interface ApiDateParts {
  month: string;
  day: string;
  hourStr: string;
  minute: string;
  secondRaw: string;
  hours: number;
  ampm: string;
  monthName: string;
}

/**
 * Parses an API ISO date string (e.g. "2026-06-03T02:10:24.7") into its
 * individual components without any timezone conversion.
 *
 * @param apiDate - ISO date string from the API
 * @returns Parsed date/time parts, or null when input is falsy
 */
const parseApiDateParts = (apiDate: string): ApiDateParts | null => {
  if (!apiDate) {
    return null;
  }

  const parts = apiDate.split('T');
  const datePart = parts[0] ?? '';
  const timePart = parts[1] ?? '';

  const dateParts = datePart.split('-');
  const month = dateParts[1] ?? '01';
  const day = dateParts[2] ?? '01';

  const timeParts = timePart.split(':');
  const hourStr = timeParts[0] ?? '0';
  const minute = timeParts[1] ?? '00';
  const secondRaw = (timeParts[2] ?? '00').replace('Z', '');

  let hours = Number.parseInt(hourStr, 10);
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;

  const monthName = MONTH_NAMES[Number.parseInt(month, 10) - 1] ?? '';

  return { month, day, hourStr, minute, secondRaw, hours, ampm, monthName };
};

/**
 * Formats an API ISO date string (e.g. "2026-06-03T02:10:24.7") into a
 * human-readable string of the form "Jun 03, 2:10:24 AM" without any timezone
 * conversion — the date/time components are used exactly as supplied by the API.
 *
 * AM/PM rule:
 *   - hour  0–11 → AM, displayed as-is (0 becomes 12)
 *   - hour 12–23 → PM, displayed as (hour % 12) or 12 when hour === 12
 *
 * @param apiDate - ISO date string from the API
 * @returns Formatted string e.g. "Jun 03, 2:10:24 AM", or "" when input is falsy
 */
export const formatApiDate = (apiDate: string): string => {
  const parsed = parseApiDateParts(apiDate);
  if (!parsed) {
    return '';
  }
  const { day, hours, minute, secondRaw, ampm, monthName } = parsed;
  const second = (secondRaw.split('.')[0] ?? '00').padStart(2, '0');
  return `${monthName} ${day}, ${hours}:${minute}:${second} ${ampm}`;
};

/**
 * Formats an API ISO date string (e.g. "2026-06-03T02:10:24.7") into a
 * human-readable string of the form "Jun 03, 2:10 AM" without any timezone
 * conversion — the date/time components are used exactly as supplied by the API.
 *
 * AM/PM rule:
 *   - hour  0–11 → AM, displayed as-is (0 becomes 12)
 *   - hour 12–23 → PM, displayed as (hour % 12) or 12 when hour === 12
 *
 * @param apiDate - ISO date string from the API
 * @returns Formatted string e.g. "Jun 03, 2:10 AM", or "" when input is falsy
 */
export const formatApiDateWithOutSec = (apiDate: string): string => {
  const parsed = parseApiDateParts(apiDate);
  if (!parsed) {
    return '';
  }
  const { day, hours, minute, ampm, monthName } = parsed;
  return `${monthName} ${day}, ${hours}:${minute} ${ampm}`;
};

/**
 * Parses an ISO date string into a UTC Date object.
 *
 * @param s - ISO date string (e.g. "2026-06-03T02:10:24.7")
 * @returns UTC Date instance
 */
const parseISOString = (s: string): Date => {
  const b = s?.split(/\D+/);
  const ms = Number.parseInt((b[6] || '0').substring(0, 3), 10);
  return new Date(
    Date.UTC(
      Number.parseInt(b[0] || '0', 10),
      Number.parseInt(b[1] || '0', 10) - 1,
      Number.parseInt(b[2] || '0', 10),
      Number.parseInt(b[3] || '0', 10),
      Number.parseInt(b[4] || '0', 10),
      Number.parseInt(b[5] || '0', 10),
      ms,
    ),
  );
};

export const formatStandardHours = (value?: number | string | null): string => {
  if (value == null || value === '') return '--:-- Hrs';
  const str = String(value);
  const [hPart = '0', mPart = '0'] = str.split('.');
  const hh = hPart.padStart(2, '0');
  const mm = mPart.padEnd(2, '0');
  return `${hh}:${mm} Hrs`;
};

export const useCommonController = () => {
  const { t } = useTranslation();

  /**
   * This method is used to get the screen/ device type [i.e., Mobile/Tab]
   * @returns {boolean} - true if portrait/mobile, false if landscape
   */
  const useScreenOrientation = () => {
    const { width } = useWindowDimensions();

    // On web, debounce the width so that layout/API-triggering re-renders only
    // happen once the user has stopped resizing (300 ms idle), not on every pixel.
    const [debouncedWidth, setDebouncedWidth] = useState(width);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
      if (Platform.OS !== 'web') {
        setDebouncedWidth(width);
        return;
      }
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        setDebouncedWidth(width);
      }, 300);
      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
      };
    }, [width]);

    const isMobile = debouncedWidth <= 768;
    return isMobile;
  };

  /**
   * This method is used to get languages list
   * @param {t}  - Language Translator
   * @returns {DropdownOption[]}
   */
  const getLanguages = useMemo<DropdownOption[]>(
    () => [
      {
        label: 'English',
        value: 'en',
        accessibilityLabel: 'English',
        accessibilityRole: 'menuitem',
        accessibilityHint: 'Select English as the application language',
      },
      {
        label: 'Español',
        value: 'es',
        accessibilityLabel: 'Español',
        accessibilityRole: 'menuitem',
        accessibilityHint: 'Select Spanish as the application language',
      },
    ],
    [t],
  );

  /**
   * This method is used to get the selected language object
   * @param {language} - selected language
   * @returns {DropdownOption}
   */
  const getSelectedLanguage = useCallback(
    (language: string) => {
      return getLanguages?.filter(
        (lan: DropdownOption) => lan.value === language,
      );
    },
    [getLanguages],
  );

  /**
   * This method is used to get the formatted date
   * @param {isoDate} - string
   * @returns {string}
   */
  const getFormattedMonthDay = (isoDate: string) => {
    if (!isoDate) {
      return '';
    }

    const date = parseISOString(isoDate);

    const dateOptions: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: '2-digit',
    };

    const datePart = date.toLocaleDateString('en-US', dateOptions);

    return `${datePart}`;
  };

  /**
   * This method is used to get the formatted date
   * @param {isoDate} - string
   * @returns {string}
   */
  const getFormattedDate = (isoDate: string) => {
    if (!isoDate) {
      return '';
    }

    const date = parseISOString(isoDate);

    const dateOptions: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: '2-digit',
    };

    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    };

    const datePart = date.toLocaleDateString('en-US', dateOptions);
    const timePart = date.toLocaleTimeString('en-US', timeOptions);

    return `${datePart}, ${timePart}`;
  };

  /**
   * This method is used to sanitize input based on validation type
   * @param {value} - string input to sanitize
   * @param {validationType} - type of validation to apply
   * @returns {string} - sanitized string based on validation type
   * @example
   * sanitizeInput('Hello123', 'alphabets') // returns 'Hello'
   * sanitizeInput('Hello123', 'numbers') // returns '123'
   * sanitizeInput('Hello@123', 'alphanumeric') // returns 'Hello123'
   */
  const sanitizeInput = useCallback((value: string, validationType: 'alphabets' | 'numbers' | 'alphanumeric' = 'alphanumeric'): string => {
    switch (validationType) {
      case 'alphabets':
        // Only allow letters and spaces
        return value.replace(/[^a-zA-Z\s]/g, ''); // NOSONAR
      case 'numbers':
        // Only allow numbers
        return value.replace(/[^0-9]/g, ''); // NOSONAR
      case 'alphanumeric':
        // Allow letters, numbers, and spaces
        return value.replace(/[^a-zA-Z0-9\s]/g, ''); // NOSONAR
      default:
        return value;
    }
  }, []);

  return {
    t,
    useScreenOrientation,
    getLanguages,
    getSelectedLanguage,
    getFormattedDate,
    getFormattedMonthDay,
    sanitizeInput,
    formatApiDate
  };
};
